import { useState } from "react";
import { toast } from "sonner";
import {
  ClipboardCheck, Bell, PlusCircle, PartyPopper, ArrowRight, ArrowLeft, Landmark,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import KinglyLogo from "@/components/KinglyLogo";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const ASSISTANT_SLIDES = [
  {
    icon: PartyPopper,
    title: "Welcome to the team!",
    body: (name) => (
      <>Hi {name || "there"} — you&apos;re set up as an <b>Assistant</b>. Your job in Kingly is
      simple: <b>see what each horse needs, do it, and check it off.</b> The trainer handles
      billing and scheduling — you keep the barn running.</>
    ),
  },
  {
    icon: ClipboardCheck,
    title: "The whiteboard is your day",
    body: () => (
      <>Open a show → <b>Show Day → Whiteboard</b>: horses down the left, tasks across
      the top — just like the dry-erase board at the barn. <b>Tap a box to check it off</b> when
      it&apos;s done. Tap <b>&ldquo;Only mine&rdquo;</b> to see just the tasks assigned to you, and
      use <b>Show-day mode</b> for giant ringside buttons.</>
    ),
  },
  {
    icon: Bell,
    title: "Never miss a timed task",
    body: () => (
      <>Tasks can have a set time (like &ldquo;Lunge at 7:30 AM&rdquo;). You&apos;ll get a
      <b> text and a notification 15 minutes before</b> anything assigned to you is due.
      Enable push notifications in <b>Team &amp; Settings → Notifications</b> so nothing slips by.</>
    ),
  },
  {
    icon: PlusCircle,
    title: "Log what you do",
    body: () => (
      <>Did a braiding, lunging, or gave meds? Tap <b>&ldquo;Log service&rdquo;</b> on the
      whiteboard, pick the horse and what you did — takes 5 seconds. This is how the barn keeps
      records straight, so log it every time. That&apos;s it — you&apos;re ready!</>
    ),
  },
];

const BOOKKEEPER_SLIDES = [
  {
    icon: PartyPopper,
    title: "Welcome — you're the Bookkeeper",
    body: (name) => (
      <>Hi {name || "there"} — you&apos;re set up as the barn&apos;s <b>Bookkeeper</b>. You&apos;ll only
      see <b>Books</b> and Help: no horses, shows, whiteboard or invoices. Everything here is
      <b> cash-basis</b> — money in, money out, plain-English categories.</>
    ),
  },
  {
    icon: ClipboardCheck,
    title: "Register & categories",
    body: () => (
      <>The <b>Register</b> lists every dollar in and out with a running balance. Pick a category
      right in the row — they&apos;re pre-mapped to Schedule C lines. Add or rename categories any
      time under <b>Categories</b>.</>
    ),
  },
  {
    icon: Landmark,
    title: "Bank, Reconcile, P&L",
    body: () => (
      <>Import the bank&apos;s CSV statement under <b>Bank</b>, then <b>Match / Add / Exclude</b> each line.
      <b> Reconcile</b> checks the statement&apos;s ending balance to the penny, and <b>P&amp;L</b> gives
      you income and expenses by category with a CSV for tax time. That&apos;s it!</>
    ),
  },
];

export const AssistantTutorial = ({ onDone }) => {
  const { user } = useAuth();
  const [i, setI] = useState(0);
  const SLIDES = user?.role === "bookkeeper" ? BOOKKEEPER_SLIDES : ASSISTANT_SLIDES;
  const slide = SLIDES[i];
  const Icon = slide.icon;
  const last = i === SLIDES.length - 1;

  const finish = async () => {
    try { await api.post("/auth/tutorial-done"); } catch (e) { console.warn("tutorial-done save failed (non-blocking)", e); }
    toast.success("You're all set — welcome aboard!");
    onDone();
  };

  return (
    <div className="fixed inset-0 z-[70] bg-slate-900/95 backdrop-blur-sm flex items-center justify-center p-5" data-testid="assistant-tutorial">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-7 space-y-5">
        <div className="flex items-center justify-between">
          <KinglyLogo size={34} />
          <button onClick={finish} data-testid="tutorial-skip" className="text-xs text-slate-400 hover:text-slate-600">
            Skip tour
          </button>
        </div>
        <div className="w-14 h-14 rounded-2xl bg-amber-100 flex items-center justify-center">
          <Icon className="w-7 h-7 text-amber-600" />
        </div>
        <div>
          <h2 className="font-display font-bold text-2xl text-slate-900">{slide.title}</h2>
          <p className="text-sm text-slate-600 leading-relaxed mt-2" data-testid="tutorial-body">
            {slide.body(user?.name?.split(" ")[0])}
          </p>
        </div>
        <div className="flex items-center gap-1.5" data-testid="tutorial-dots">
          {SLIDES.map((s, d) => (
            <span key={s.title} className={`h-1.5 rounded-full transition-all ${d === i ? "w-6 bg-amber-500" : "w-1.5 bg-zinc-200"}`} />
          ))}
        </div>
        <div className="flex gap-2">
          {i > 0 && (
            <Button variant="outline" onClick={() => setI(i - 1)} data-testid="tutorial-back">
              <ArrowLeft className="w-4 h-4" />
            </Button>
          )}
          <Button onClick={last ? finish : () => setI(i + 1)} data-testid="tutorial-next"
            className="flex-1 bg-slate-900 hover:bg-slate-800 text-amber-300 font-semibold h-11">
            {last ? "Got it — let's go!" : <>Next <ArrowRight className="w-4 h-4 ml-1.5" /></>}
          </Button>
        </div>
      </div>
    </div>
  );
};
